/**
 * Push Notification Service
 * Handles push notification registration and events
 */

import { PushNotifications } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import { API_CONFIG } from '../config/environment';
import { info, warn, error, debug } from '../utils/logger';

let pushNotificationInitialized = false;
let deviceToken = null;
let notificationListeners = {
  registration: null,
  registrationError: null,
  pushNotificationReceived: null,
  pushNotificationActionPerformed: null
};

/**
 * Initialize push notifications
 */
export const initializePushNotifications = async () => {
  if (!Capacitor.isNativePlatform()) {
    info('Push notifications only available on native platforms', { platform: 'web' });
    return { success: false, reason: 'web_platform' };
  }

  if (pushNotificationInitialized) {
    info('Push notifications already initialized', { token: deviceToken ? 'exists' : 'none' });
    return { success: true, token: deviceToken };
  }

  try {
    // Request permissions
    const permissionResult = await PushNotifications.requestPermissions();

    if (permissionResult.receive !== 'granted') {
      warn('Push notification permissions denied', { permissionResult });
      return { success: false, reason: 'permissions_denied' };
    }

    // Register for push notifications
    await PushNotifications.register();

    // Set up listeners
    setupNotificationListeners();

    pushNotificationInitialized = true;
    return { success: true };
  } catch (err) {
    error('Error initializing push notifications', { error: err.message, stack: err.stack });
    return { success: false, error: err.message };
  }
};

/**
 * Set up push notification event listeners
 */
const setupNotificationListeners = () => {
  // Registration successful
  notificationListeners.registration = PushNotifications.addListener('registration', (token) => {
    info('Push registration success', { tokenLength: token.value?.length || 0 });
    deviceToken = token.value;
    registerTokenWithBackend(token.value);
  });

  // Registration failed
  notificationListeners.registrationError = PushNotifications.addListener('registrationError', (err) => {
    error('Error on registration', { error: err });
  });

  // Notification received while app is in foreground
  notificationListeners.pushNotificationReceived = PushNotifications.addListener(
    'pushNotificationReceived',
    (notification) => {
      debug('Push notification received', { notificationId: notification.id, title: notification.title });
      // Handle notification display
      handleNotificationReceived(notification);
    }
  );

  // Notification tapped
  notificationListeners.pushNotificationActionPerformed = PushNotifications.addListener(
    'pushNotificationActionPerformed',
    (action) => {
      info('Push notification action performed', { actionId: action.actionId, notificationId: action.notification?.id });
      handleNotificationTapped(action);
    }
  );
};

/**
 * Register device token with backend
 */
const registerTokenWithBackend = async (token) => {
  try {
    const userToken = localStorage.getItem('token');
    if (!userToken) {
      warn('No user token found, skipping push registration');
      return;
    }

    const response = await fetch(`${API_CONFIG.baseUrl}/push-notifications/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userToken}`
      },
      body: JSON.stringify({
        deviceToken: token,
        platform: Capacitor.getPlatform()
      })
    });

    if (!response.ok) {
      throw new Error('Failed to register token');
    }

    info('Push token registered with backend', { platform: Capacitor.getPlatform() });
  } catch (err) {
    error('Error registering push token', { error: err.message, stack: err.stack });
  }
};

/**
 * Handle notification received
 */
const handleNotificationReceived = (notification) => {
  // You can customize notification handling here
  // For example, show a custom notification UI
  debug('Notification received', {
    id: notification.id,
    title: notification.title,
    body: notification.body
  });
};

/**
 * Handle notification tapped
 */
const handleNotificationTapped = (action) => {
  // Handle navigation or actions when notification is tapped
  info('Notification tapped', {
    actionId: action.actionId,
    notificationId: action.notification?.id,
    route: action.notification.data?.route
  });

  // Example: Navigate to specific page based on notification data
  if (action.notification.data?.route) {
    window.location.href = action.notification.data.route;
  }
};

/**
 * Register token with backend (call after user login)
 */
export const registerPushToken = async (userId) => {
  if (!deviceToken || !Capacitor.isNativePlatform()) {
    return { success: false };
  }

  try {
    const userToken = localStorage.getItem('token');
    if (!userToken) {
      return { success: false, reason: 'no_auth_token' };
    }

    const response = await fetch(`${API_CONFIG.baseUrl}/push-notifications/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userToken}`
      },
      body: JSON.stringify({
        deviceToken: deviceToken,
        platform: Capacitor.getPlatform(),
        userId: userId
      })
    });

    if (!response.ok) {
      throw new Error('Failed to register token');
    }

    return { success: true };
  } catch (err) {
    error('Error registering push token', { error: err.message, userId, stack: err.stack });
    return { success: false, error: err.message };
  }
};

/**
 * Remove notification listeners (cleanup)
 */
export const removeNotificationListeners = () => {
  Object.values(notificationListeners).forEach(listener => {
    if (listener) {
      listener.remove();
    }
  });
  
  notificationListeners = {
    registration: null,
    registrationError: null,
    pushNotificationReceived: null,
    pushNotificationActionPerformed: null
  };
  
  pushNotificationInitialized = false;
};

/**
 * Get current device token
 */
export const getDeviceToken = () => {
  return deviceToken;
};

